import { forwardRef, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { useGetAccount } from '@features/user/hooks/useGetAccount';
import { useListNewsletterTypes } from '@features/user/hooks/useListNewsletterTypes';
import { useUpdateCommunications } from '@features/user/hooks/useUpdateCommunications';
import { Wizard, type WizardRefMethods } from '@goodfynd/forms';
import { Loading } from '@goodfynd/ui';
import type { BaseResult } from 'types/api';

import { StepNewsletters, StepNewslettersSummary } from './StepNewsletters';
import { StepTopics, StepTopicsSummary } from './StepTopics';
import type {
  CommunicationsPreferencesWizardFields,
  CommunicationsPreferencesWizardProps,
} from './types';
import { newslettersValidation, topicsValidation } from './validation';

export const CommunicationsPreferencesWizard = forwardRef(
  function CommunicationsPreferencesWizard(
    { onExit, ...props }: CommunicationsPreferencesWizardProps,
    ref: React.ForwardedRef<WizardRefMethods | null>
  ) {
    const { user } = useApp();

    const { newsletterTypeOptions, isFetching: newsletterTypesAreFetching } =
      useListNewsletterTypes();

    const onSuccess = useCallback(() => {
      onExit?.();
    }, [onExit]);

    const { data, isFetching: isFetchingUser } = useGetAccount({
      id: user?.id,
    });

    const { isUpdating, updateCommunications } = useUpdateCommunications({
      onSuccess,
    });

    const getInitialData = useCallback(():
      | Partial<CommunicationsPreferencesWizardFields>
      | undefined => {
      if (!data) {
        return undefined;
      }

      const { communications } = data;
      const { organizations, subscriptions, vendors, venues } = communications;
      return {
        email: false,
        push: false,
        sms: false,
        newsletters: newsletterTypeOptions
          .filter((option) => subscriptions.includes(Number(option.value)))
          .map((option) => Number(option.value)),
        organizationAlerts: organizations.map((organization) => ({
          label: organization,
          value: organization,
        })),
        vendorAlerts: vendors.map((vendor) => ({
          label: vendor,
          value: vendor,
        })),
        venueAlerts: venues.map((venue) => ({
          label: venue,
          value: venue,
        })),
      };
    }, [data, newsletterTypeOptions]);

    if (isFetchingUser || newsletterTypesAreFetching) {
      return <Loading />;
    }

    return (
      <Wizard<CommunicationsPreferencesWizardFields, BaseResult>
        {...props}
        compact
        initialData={getInitialData()}
        onExit={onExit}
        ref={ref}
        showHelpIcon={false}
        onSubmit={async ({
          newsletters,
          organizationAlerts,
          vendorAlerts,
          venueAlerts,
        }) => {
          return updateCommunications({
            organizations: organizationAlerts.map((alert) =>
              String(alert.nameId ?? alert.value)
            ),
            vendors: vendorAlerts.map((alert) =>
              String(alert.nameId ?? alert.value)
            ),
            venues: venueAlerts.map((alert) =>
              String(alert.nameId ?? alert.value)
            ),
            subscriptions: newsletters,
          });
        }}
        steps={[
          {
            title: 'Newsletter subscriptions',
            schema: newslettersValidation,
            renderStep: () => (
              <StepNewsletters newsletterOptions={newsletterTypeOptions} />
            ),
            renderSummary: () => (
              <StepNewslettersSummary
                newsletterOptions={newsletterTypeOptions}
              />
            ),
          },
          {
            title: 'Topics',
            schema: topicsValidation,
            renderStep: () => <StepTopics />,
            renderSummary: () => <StepTopicsSummary />,
          },
          // {
          //   title: 'Channels',
          //   schema: channelsValidation,
          //   renderStep: () => <StepChannels />,
          //   renderSummary: () => <StepChannelsSummary />,
          // },
        ]}
        submitProps={{
          busy: isUpdating,
        }}
      />
    );
  }
);
